import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTable from 'react-table';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { get } from 'helpers/apiHelpers';
import { ALL_SELECTED_VALUE } from 'components/SelectAll';
import RegularCheckbox from 'components/Checkbox/RegularCheckbox';
import TotalResults from 'components/DataGrid/TotalResults';

import DATA_TYPES from './constants/dataTypes';
import DEFAULT_MULTIPLIER from './constants/defaultMultiplier';
import ShopDishesRow from './ShopDishesRow';
import MultiplierHeader from './MultiplierHeader';
import MultiplierInput from './MultiplierInput/MultiplierInput';
import SelectionBar from './SelectionBar/SelectionBar';
import MultiSelectFilter from './MultiSelectFilter';

const SHOP_DISHES_VALUE = 'shopDishes';

const MULTIPLIERS = 'multipliers';

const useStyles = makeStyles({
  columnManager: {
    marginRight: '10px',
  },
  checkbox: {
    '&&': {
      padding: '0 0 0 3px',
    },
  },
  table: {
    '& .rt-tr-group': {
      height: '56px',
    },
    '& .rt-thead.-filters': {
      minHeight: '44px',
    },
    '& .rt-thead.-filters input[type=checkbox]': {
      all: 'revert',
    },
    '& .rt-thead.-filters .rt-th': {
      paddingTop: '0',
      display: 'flex',
      alignItems: 'end',
      justifyContent: 'stretch',
      '& > div': {
        flexGrow: '1',
      },
    },
    '& .-loading.-active': {
      zIndex: '0',
    },
  },
  selectionBar: {
    width: 'calc(100% + 30px)',
    margin: '0 0 -8px -15px',
  },
});

const fetchData = (endpoint, brands) =>
  brands.map(({ value }) => {
    const brandId = value.split('/brands/')[1];

    return get(
      endpoint,
      { pagination: false },
      {
        headers: { 'X-G-BRAND': brandId },
      }
    );
  });

const getFlattenedData = (brandGroups, shopModule) => {
  const result = [];

  brandGroups.forEach((brandGroup, brandGroupIndex) => {
    if (shopModule[brandGroupIndex].isShopDishesAvailable) {
      result.push({
        brandIri: shopModule[brandGroupIndex].brandIri,
        brandName: shopModule[brandGroupIndex].brandName,
        dietName: <ShopDishesRow />,
        dietIri: SHOP_DISHES_VALUE,
        calorificName: '-',
        variantName: '-',
        multiplier: DEFAULT_MULTIPLIER,
        checked: false,
        type: DATA_TYPES.shopDishesItem,
      });
    }

    brandGroup.forEach(
      ({ brandIri, brandName, dietIri, dietName, variants }) => {
        variants.forEach(variant => {
          variant.calories.forEach(calorific => {
            result.push({
              brandIri,
              brandName,
              dietIri,
              dietName,
              variantIri: variant.variantIri,
              variantName: variant.variantName,
              calorificIri: calorific.calorificIri,
              calorificName: calorific.calorificName,
              multiplier: DEFAULT_MULTIPLIER,
              checked: false,
              type: DATA_TYPES.item,
            });
          });
        });
      }
    );
  });
  return result;
};

const fetchShopConfiguration = brands =>
  brands.map(({ value }) => {
    const brandId = value.split('/brands/')[1];

    return get(`/brands/${brandId}/modules`, {
      module: ['Shop'],
    });
  });

const getSelectFilterMethod = (value, row, accessor) => {
  if (value.length === 0) {
    return true;
  }

  return value.includes(row[accessor]);
};

const applyFilters = (data, filters, columns) =>
  data.filter(row =>
    filters.every(({ id, value }) => {
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && (value.length === 0 || value.includes('*')))
      ) {
        return true;
      }

      const column = columns.find(col => col.id === id);

      if (column.filterable !== true || typeof column.accessor !== 'string') {
        return true;
      }

      if (typeof column.filterMethod === 'function') {
        return column.filterMethod({ value }, row);
      }

      const rowValue = row[column.accessor];

      if (Array.isArray(value)) {
        return value.includes(rowValue);
      }

      return String(rowValue)
        .toLowerCase()
        .includes(String(value).toLowerCase());
    })
  );

const setStorage = (key, newItem, rowIndex) => {
  const existing = localStorage.getItem(key);
  const data = existing ? JSON.parse(existing) : [];

  // eslint-disable-next-line no-underscore-dangle -- FIXME
  const { multiplier: _newMultiplier, ...newItemWithoutMultiplier } = newItem;

  const index = data.findIndex(item => {
    // eslint-disable-next-line no-underscore-dangle -- FIXME
    const { multiplier: _multiplier, ...itemWithoutMultiplier } = item;

    return (
      JSON.stringify(itemWithoutMultiplier) ===
      JSON.stringify({ ...newItemWithoutMultiplier, rowIndex })
    );
  });

  // eslint-disable-next-line no-negated-condition -- FIXME
  if (index !== -1) {
    data[index] = { ...newItem, rowIndex };
  } else {
    data.push({ ...newItem, rowIndex });
  }

  localStorage.setItem(key, JSON.stringify(data));
};

const MultiplierMatrix = ({
  data,
  setData,
  selectedBrands,
  rowIndex,
  rowKey,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [dietsOptions, setDietsOptions] = useState([]);

  const [multiplierPopperAnchorEl, setMultiplierPopperAnchorEl] =
    useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [filters, setFilters] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const checkedData = data.filter(({ checked }) => checked);

  const columns = [
    {
      id: 'toggleCheckAll',
      width: 50,
      filterable: false,
      sortable: false,
      Header: () => (
        <RegularCheckbox
          customClasses={{ root: classes.checkbox }}
          checked={
            // eslint-disable-next-line no-use-before-define -- FIXME
            paginatedData.length > 0 &&
            // eslint-disable-next-line no-use-before-define -- FIXME
            paginatedData.every(({ checked }) => checked)
          }
          onChange={e => {
            setData(prev => ({
              ...prev,
              [rowKey]: prev[rowKey].map(item =>
                // eslint-disable-next-line no-use-before-define -- FIXME
                paginatedData.some(
                  ({ brandIri, dietIri, calorificIri }) =>
                    brandIri === item.brandIri &&
                    dietIri === item.dietIri &&
                    calorificIri === item.calorificIri
                )
                  ? { ...item, checked: e.target.checked }
                  : item
              ),
            }));
          }}
        />
      ),
      accessor: ({ checked, brandIri, dietIri, calorificIri }) => (
        <RegularCheckbox
          customClasses={{ root: classes.checkbox }}
          checked={checked}
          onChange={e => {
            setData(prev => ({
              ...prev,
              [rowKey]: prev[rowKey]?.map(dataItem =>
                dataItem.brandIri === brandIri &&
                dataItem.dietIri === dietIri &&
                dataItem.calorificIri === calorificIri
                  ? { ...dataItem, checked: e.target.checked }
                  : dataItem
              ),
            }));
          }}
        />
      ),
    },
    {
      id: 'brandIri',
      Header: t('multiplierMatrix.tableHeader.brand'),
      accessor: 'brandName',
      filterable: true,
      sortable: false,
      filterMethod: ({ value }, row) =>
        getSelectFilterMethod(value, row, 'brandIri'),
      Filter: ({ onChange, filter }) => (
        <MultiSelectFilter
          onChange={onChange}
          options={selectedBrands.filter(
            ({ value }) => value !== ALL_SELECTED_VALUE
          )}
          selectedValues={filter?.value}
        />
      ),
    },
    {
      id: 'dietIri',
      Header: t('multiplierMatrix.tableHeader.diet'),
      accessor: 'dietName',
      filterable: true,
      sortable: false,
      filterMethod: ({ value }, row) =>
        getSelectFilterMethod(value, row, 'dietIri'),
      Filter: ({ onChange, filter }) => {
        const brandFilter = filters.find(({ id }) => id === 'brandIri');

        // eslint-disable-next-line no-shadow -- FIXME
        const selectedBrands = brandFilter?.value || [];
        const selectedValues = filter?.value || [];

        const optionsToShow =
          selectedBrands.length > 0
            ? dietsOptions.filter(({ brandIri, value, label }) => {
                if (value === SHOP_DISHES_VALUE) {
                  return { label, value };
                }

                return selectedBrands.includes(brandIri);
              })
            : dietsOptions;

        const availableValues = optionsToShow.map(({ value }) => value);

        const hasValidValues = selectedValues.some(value =>
          availableValues.includes(value)
        );

        if (!hasValidValues && selectedValues.length > 0) {
          onChange([]);
        }

        return (
          <MultiSelectFilter
            onChange={onChange}
            options={optionsToShow}
            selectedValues={filter?.value}
          />
        );
      },
    },
    {
      id: 'variantIri',
      Header: t('multiplierMatrix.tableHeader.variant'),
      accessor: 'variantName',
      filterable: true,
      sortable: false,
    },
    {
      id: 'calorificIri',
      Header: t('multiplierMatrix.tableHeader.calorific'),
      accessor: 'calorificName',
      filterable: true,
      sortable: false,
    },
    {
      id: 'multiplier',
      Header: <MultiplierHeader />,
      accessor: item => {
        const { type, brandIri, dietIri, calorificIri, multiplier } = item;

        return (
          <MultiplierInput
            multiplierPopperAnchorEl={multiplierPopperAnchorEl}
            setMultiplierPopperAnchorEl={setMultiplierPopperAnchorEl}
            multiplier={multiplier}
            onMultiplierChange={updatedMultiplier => {
              setData(prev => ({
                ...prev,
                [rowKey]: prev[rowKey]?.map(dataItem =>
                  dataItem.brandIri === brandIri &&
                  dataItem.dietIri === dietIri &&
                  dataItem.calorificIri === calorificIri
                    ? { ...dataItem, multiplier: updatedMultiplier }
                    : dataItem
                ),
              }));

              setStorage(
                MULTIPLIERS,
                { ...item, multiplier: updatedMultiplier },
                rowIndex
              );
            }}
            isPopperDisabled={type === DATA_TYPES.shopDishesItem}
            matrix={[
              {
                brand: brandIri,
                diets:
                  type === DATA_TYPES.item
                    ? [
                        {
                          diet: dietIri,
                          calorific: [calorificIri],
                        },
                      ]
                    : [],
              },
            ]}
          />
        );
      },
      sortable: false,
      filterable: false,
    },
  ];

  const filteredData = applyFilters(data, filters, columns);
  const paginatedData = filteredData.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  useEffect(() => {
    const sortedBrands = selectedBrands
      .filter(({ value }) => value !== '*')
      .toSorted((a, b) => {
        const brandA = a.label.toLowerCase();
        const brandB = b.label.toLowerCase();
        if (brandA < brandB) {
          return -1;
        }
        if (brandA > brandB) {
          return 1;
        }
        return 0;
      });

    (async () => {
      const responses = await Promise.all([
        ...fetchData('variants', sortedBrands),
        ...fetchData('diets', sortedBrands),
        ...fetchShopConfiguration(sortedBrands),
      ]);

      const variantsResponse = responses.filter(
        res => res['@id'] === '/variants'
      );
      const dietsResponse = responses.filter(res => res['@id'] === '/diets');
      const shopModuleResponse = responses.filter(
        res => res['@type'] === 'Brand'
      );

      const strippedVariantsResponse = variantsResponse.map(brandVariants =>
        brandVariants['hydra:member'].map(variant => ({
          variantIri: variant['@id'],
          variantName: variant.name,
          calories: variant.calories.map(calorific => ({
            calorificIri: calorific['@id'],
            calorificName: calorific.name,
          })),
        }))
      );

      const strippedDietsResponse = dietsResponse.map(
        (brandDiets, brandIndex) =>
          brandDiets['hydra:member'].map(diet => ({
            brandIri: sortedBrands[brandIndex].value,
            brandName: sortedBrands[brandIndex].label,
            dietIri: diet['@id'],
            dietName: diet.name,
            variants: diet.variants,
          }))
      );

      const dietsOptionsToSet = strippedDietsResponse
        .map(dietsGroup => {
          if (dietsGroup.length === 0) {
            return [];
          }

          return [
            {
              label: dietsGroup[0].brandName,
              isSeparator: true,
              brandIri: dietsGroup[0].brandIri,
            },
            ...dietsGroup.map(diet => ({
              label: diet.dietName,
              value: diet.dietIri,
              brandIri: dietsGroup[0].brandIri,
            })),
          ];
        })
        .flat();

      setDietsOptions([
        {
          label: t('multiplierMatrix.shopDishes'),
          value: SHOP_DISHES_VALUE,
        },
        ...dietsOptionsToSet,
      ]);

      const dietsCombinedWithCalories = strippedDietsResponse.map(
        (brandDiets, brandIndex) =>
          brandDiets.map(diet => ({
            ...diet,
            variants: diet.variants.map(variant => {
              const currentVariant = strippedVariantsResponse[brandIndex].find(
                ({ variantIri }) => variantIri === variant
              );

              return {
                variantIri: variant,
                variantName: currentVariant?.variantName,
                calories: currentVariant?.calories,
              };
            }),
          }))
      );

      const strippedShopModuleResponse = shopModuleResponse.map(
        (brandModule, brandIndex) => {
          const { allowDishesFromOffer, allowDishesFromPlaning } =
            brandModule.configuration.Shop ?? {};

          return {
            brandIri: brandModule['@id'],
            brandName: sortedBrands[brandIndex].label,
            isShopDishesAvailable:
              allowDishesFromOffer || allowDishesFromPlaning,
          };
        }
      );

      const flattened = getFlattenedData(
        dietsCombinedWithCalories,
        strippedShopModuleResponse
      );

      const storageValue = localStorage.getItem(MULTIPLIERS);
      const multipliersFromStorage = storageValue
        ? JSON.parse(storageValue).filter(item => item.rowIndex === rowIndex)
        : [];

      const mergedWithStorage = flattened.map(dataItem => {
        // eslint-disable-next-line no-underscore-dangle -- FIXME
        const {
          multiplier: _dataMultiplier,
          rowIndex: _dataIndex,
          ...dataItemWithoutMultiplier
        } = dataItem;

        const match = multipliersFromStorage.find(storageItem => {
          // eslint-disable-next-line no-underscore-dangle -- FIXME
          const {
            multiplier: _storageMultiplier,
            rowIndex: _rowIndex,
            ...storageItemWithoutMultiplier
          } = storageItem;

          return (
            JSON.stringify(storageItemWithoutMultiplier) ===
            JSON.stringify(dataItemWithoutMultiplier)
          );
        });

        return match ? { ...dataItem, multiplier: match.multiplier } : dataItem;
      });

      setData(prev => ({ ...prev, [rowKey]: mergedWithStorage }));

      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  return (
    <>
      <TotalResults
        count={paginatedData.length}
        total={data.length}
        isLoading={isLoading}
      />
      <ReactTable
        manual={true}
        filterable={true}
        resizable={false}
        loading={isLoading}
        minRows={10}
        columns={columns}
        data={paginatedData}
        className={classNames('-striped -highlight', classes.table)}
        showPaginationTop={true}
        page={page}
        pages={Math.ceil(filteredData.length / pageSize)}
        pageSize={pageSize}
        pageSizeOptions={[20, 50, 100, data.length]}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onFilteredChange={newFilters => {
          setFilters(newFilters);
        }}
        previousText={t('dataGrid.prevPage')}
        nextText={t('dataGrid.nextPage')}
        loadingText={t('dataGrid.loading')}
        noDataText={t('dataGrid.notFound')}
        pageText={t('dataGrid.page')}
        ofText={t('dataGrid.pageOf')}
        rowsText={t('dataGrid.results')}
      />
      {checkedData.length > 0 && (
        <SelectionBar
          className={classes.selectionBar}
          total={data.length}
          checkedData={checkedData}
          setData={setData}
          rowKey={rowKey}
        />
      )}
    </>
  );
};

export default MultiplierMatrix;
